import React, { useMemo } from 'react';
import i18n from '../../../../../i18n';
import { fmt } from '../../../data/metrics';
import type { DrillPanel } from '../../../types';
import { DrillBucketBarChart } from './DrillBucketBarChart';
import { DrillTile } from './DrillTile';
import { DrillDonutChart } from './charts/DrillDonutChart';

export function DrillBlockedSection({
  panel,
  bucketLabels,
  compareLabel,
}: {
  metricKey: string;
  panel: Extract<DrillPanel, { kind: 'blockedImpact' }>;
  bucketLabels?: string[];
  /** Legend label for the compare overlay. */
  compareLabel?: string;
}) {
  const blockedPct = Number.isFinite(panel.blockedPct) ? panel.blockedPct : 0;

  const inspector = useMemo(
    () => (
      <>
        {panel.series && panel.series.length > 1 && (
          <DrillBucketBarChart
            bare
            series={panel.series}
            compareSeries={panel.compareSeries}
            seriesLabel={panel.seriesLabel ?? i18n.t('performance:drillUi.blockedOverTime')}
            compareLabel={compareLabel}
            bucketLabels={bucketLabels}
            fmtVal={v => `${v}%`}
            metricKey="occupancy"
          />
        )}
        <div className="dcrux-blocked-cols" style={{ marginTop: 12 }}>
          <div>
            <div className="dcrux-col-lab">{i18n.t('performance:drillUi.byType')}</div>
            <div className="dcrux-si-list">
              {panel.byType.map(t => {
                const max = Math.max(...panel.byType.map(x => x.count), 1);
                return (
                  <div className="dcrux-si-row" key={t.label}>
                    <span className="dcrux-si-name">{t.label}</span>
                    <span className="dcrux-si-track">
                      <span
                        className="dcrux-si-fill"
                        style={{ width: `${(t.count / max) * 100}%` }}
                      />
                    </span>
                    <span className="dcrux-si-val">{fmt.num(t.count)}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <div className="dcrux-col-lab">{i18n.t('performance:drillUi.byDow')}</div>
            <div className="dcrux-si-list">
              {panel.byDow.map(d => {
                const max = Math.max(...panel.byDow.map(x => x.events), 1);
                return (
                  <div className="dcrux-si-row" key={d.dow}>
                    <span className="dcrux-si-name">{d.label}</span>
                    <span className="dcrux-si-track">
                      <span
                        className="dcrux-si-fill"
                        style={{ width: `${(d.events / max) * 100}%` }}
                      />
                    </span>
                    <span className="dcrux-si-val">{fmt.num(d.events)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {panel.note ? (
          <p className="dcrux-peak-line" style={{ marginTop: 10 }}>
            {panel.note}
          </p>
        ) : null}
      </>
    ),
    [panel, bucketLabels],
  );

  return (
    <DrillTile
      id="drill-section-blocked-impact"
      sectionId="blocked"
      span={4}
      title={panel.title}
      meta={i18n.t('performance:drillUi.ofOpenSeatHours')}
      inspector={inspector}
      n={i18n.t('performance:drillUi.nBlocks', { n: fmt.num(panel.totalEvents) })}
    >
      <div className="ddrill-stat-inline">
        <strong
          style={{
            color: blockedPct > 10 ? 'var(--st-alert)' : 'var(--ops-ink)',
          }}
        >
          {blockedPct}%
        </strong>
        <span>{i18n.t('performance:drillUi.ofSeatHours')}</span>
      </div>
      <DrillDonutChart
        slices={[
          {
            name: i18n.t('performance:drillUi.blocked'),
            value: Math.max(panel.seatHours, 0.01),
            color: 'var(--st-alert)',
          },
          {
            name: i18n.t('performance:drillUi.available'),
            value: Math.max(
              panel.availableSeatHours - panel.seatHours,
              0.01,
            ),
            color: 'var(--st-steady)',
          },
        ]}
        height={120}
        innerRadius={32}
        outerRadius={48}
      />
    </DrillTile>
  );
}
